import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import isEmpty from 'lodash/isEmpty';

import {
    IconCloseOutline,
    Input,
    Adornment,
    AdornedInput,
    Button,
    Flex,
    FormHelp,
    Label,
    Spacer,
    TextButton,
} from '@zillow/constellation';

import AnalyticsConstants from 'app/shared/constants/AnalyticsConstants';
import appLogger from 'app/shared/loggers/appLogger';
import NotificationConstants from 'app/shared/constants/NotificationConstants';
import PostingActions from 'app/shared/actions/PostingActions';

import { StyledAddAmenityInput, StyledSpacer, StyledAdornment } from './StyledComponents';

const propTypes = {
    label: PropTypes.string.isRequired,
    listingAlias: PropTypes.string.isRequired,
    previouslySavedAmenities: PropTypes.array.isRequired,
    updateFormData: PropTypes.func.isRequired,
    errorObj: PropTypes.object.isRequired,
    updateErrorObj: PropTypes.func.isRequired,
    updateStatusMessage: PropTypes.func.isRequired,
};

const AdditionalAmenities = ({
    label,
    listingAlias,
    previouslySavedAmenities = [],
    updateFormData,
    errorObj,
    updateErrorObj,
    updateStatusMessage,
}) => {
    const dispatch = useDispatch();

    const [tags, setTags] = useState([...previouslySavedAmenities]);
    const [tagToAdd, setTagToAdd] = useState('');
    const [editingIndex, setEditingIndex] = useState(null);
    const [duplicateTagError, setDuplicateTagError] = useState(false);

    useEffect(() => {
        updateFormData({ amenities: tags });
    }, [tags]);

    const addNewTagOnChangeHandler = (e) => {
        if (duplicateTagError) {
            setDuplicateTagError(false);
        }
        setTagToAdd(e.target.value);
    };

    const createAmenityOnBlurHandler = () => {
        if (isEmpty(tagToAdd)) {
            updateErrorObj({ additionalAmenities: null });
            updateStatusMessage('', null);
        }
    };

    const editingOnChangeHandler = (e, index) => {
        const tagsArray = [...tags];
        tagsArray[index].amenity = e.target.value;
        setTags(tagsArray);
    };

    const createAmenity = () => {
        const isDuplicateOfSavedTag = tags.some(tag => tag.amenity === tagToAdd);

        if (isDuplicateOfSavedTag) {
            setDuplicateTagError(true);
            return;
        }

        updateStatusMessage('Saving...');
        dispatch(PostingActions
            .property
            .amenity
            .create(
                {
                    propertyId: listingAlias,
                    amenityText: tagToAdd,
                },
                AnalyticsConstants.LISTING_COMPONENT_GROUP_NAME.AMENITIES
            ))
            .then((result) => {
                updateErrorObj({ additionalAmenities: null });
                updateStatusMessage('Amenity created!', NotificationConstants.SUCCESS);
                setTags([...tags, { id: result.response.amenityId, amenity: tagToAdd }]);
                setTagToAdd('');
            })
            .catch((error) => {
                updateErrorObj({ additionalAmenities: error.message });
                const clientErrMsg = 'There was an error adding your amenity. Please try again.';
                updateStatusMessage(clientErrMsg, NotificationConstants.ERROR);
                appLogger.logError({
                    message: error.message,
                    context: 'PropertyAmenities.createAmenity',
                });
            });
    };

    const deleteAmenity = (index) => {
        const tagToDelete = tags[index];
        dispatch(PostingActions
            .property
            .amenity
            .delete(tagToDelete.id))
            .then((response) => {
                if (response.success) {
                    const tagsCopy = [...tags];
                    tagsCopy.splice(index, 1);
                    setTags(tagsCopy);
                } else {
                    const clientErrMsg = 'There was an error deleting your amenity. Please try again.';
                    updateStatusMessage(clientErrMsg, NotificationConstants.ERROR);
                    appLogger.logError({
                        message: response.error.message,
                        context: 'PropertyAmenities.deleteAmenity',
                    });
                }
            });
    };

    const updateAmenity = (index) => {
        updateStatusMessage('Saving...');
        const tagToUpdate = tags[index];
        dispatch(PostingActions
            .property
            .amenity
            .update(
                {
                    amenityId: tagToUpdate.id,
                    amenityText: tagToUpdate.amenity,
                }
            ))
            .then((result) => {
                updateErrorObj({ additionalAmenities: null });
                updateStatusMessage('Amenity saved!', NotificationConstants.SUCCESS);
                const tagsArrayCopy = [...tags];
                // Update recently added amenity with id from API.
                tagsArrayCopy[index] = {
                    ...tagsArrayCopy[index],
                    id: result.response.amenityId,
                };
                setTags(tagsArrayCopy);
            })
            .catch((error) => {
                updateErrorObj({ additionalAmenities: error.message });
                const clientErrMsg = 'There was an error updating your amenity. Please try again.';
                updateStatusMessage(clientErrMsg, NotificationConstants.ERROR);
                appLogger.logError({
                    message: error.message,
                    context: 'PropertyAmenities.updateAmenity',
                });
            })
            .finally(() => setEditingIndex(null));
    };

    const handleCreateAmenityKeyUp = (e) => {
        const keyCode = e.which || e.keyCode;
        if (keyCode === 13) {
            createAmenity();
        }
    };

    const handleSaveTagKeyUp = (e, index) => {
        const keyCode = e.which || e.keyCode;
        if (keyCode === 13) {
            updateAmenity(index);
        }
    };

    const handleDeleteAmenityKeyUp = (e, index) => {
        const keyCode = e.which || e.keyCode;
        if (keyCode === 13) {
            deleteAmenity(index);
        }
    };

    return (
        <div>
            <Spacer marginY="xs" />
            <Label>{label}</Label>
            <Spacer marginY="xs" />
            {tags.map((tag, i) => (
                <div key={`amenity-tag-${i}`}>
                    <Flex display="flex">
                        <AdornedInput
                            size="sm"
                            inputId={`AdornedInput-AmenityTag-${i}`}
                            disabled={editingIndex !== i}
                            input={(
                                <Input
                                    size="sm"
                                    onChange={e => editingOnChangeHandler(e, i)}
                                    onKeyUp={e => handleSaveTagKeyUp(e, i)}
                                    value={tag.amenity}
                                />
                            )}
                            rightAdornment={
                                editingIndex !== i ? (
                                    <StyledAdornment
                                        notEditing={editingIndex !== i}
                                        onClick={() => deleteAmenity(i)}
                                        onKeyUp={e => handleDeleteAmenityKeyUp(e, i)}
                                        tabIndex={editingIndex !== i ? 0 : ''}
                                    >
                                        <IconCloseOutline>
                                            <title>Delete</title>
                                        </IconCloseOutline>
                                    </StyledAdornment>
                                ) : <Adornment><StyledSpacer /></Adornment>
                            }
                        />
                        <Spacer marginX="xs" />
                        {editingIndex === i && (
                            <React.Fragment>
                                <TextButton
                                    onClick={() => updateAmenity(i)}
                                    style={{ fontWeight: 400 }}
                                >
                                    Save
                                </TextButton>
                                <Spacer marginX="xs" />
                            </React.Fragment>
                        )}
                        {editingIndex !== i && (
                            <React.Fragment>
                                <TextButton
                                    onClick={() => setEditingIndex(i)}
                                    style={{ fontWeight: 400 }}
                                >
                                    Edit
                                </TextButton>
                                <Spacer marginX="xs" />
                            </React.Fragment>
                        )}
                    </Flex>
                    <Spacer marginY="sm" />
                </div>
            ))}
            <Flex display="flex">
                <StyledAddAmenityInput
                    aria-label="Describe amenity"
                    placeholder="Describe amenity"
                    name="additionalAmenities"
                    size="sm"
                    onChange={addNewTagOnChangeHandler}
                    onKeyUp={handleCreateAmenityKeyUp}
                    onBlur={createAmenityOnBlurHandler}
                    value={tagToAdd}
                    error={duplicateTagError || errorObj.additionalAmenities}
                />
                <Spacer marginX="xs" />
                <Button onClick={createAmenity} disabled={tagToAdd === ''}>Add</Button>
            </Flex>
            {
                (duplicateTagError && <FormHelp error={true}>Unable to save duplicate tags</FormHelp>) ||
                (errorObj.additionalAmenities &&
                    (
                        <FormHelp
                            error={errorObj.additionalAmenities}
                            dangerouslySetInnerHTML={{ __html: errorObj.additionalAmenities }}
                        />
                    )
                )
            }
            <Spacer marginY="xs" />
        </div>
    );
};

AdditionalAmenities.propTypes = propTypes;

export default AdditionalAmenities;
